import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { MapContainer } from "react-leaflet";
import { MapContent } from "./MapContent";
import { supabase } from "./supabaseClient";
import { AuthModal } from "./AuthModal";
import { dbCreateGame, dbSaveRound, dbUpdateGame } from "./db";
import HistoryScreen from "./screens/HistoryScreen";
import LeaderboardScreen from "./screens/LeaderboardScreen";
import ProfileScreen from "./screens/ProfileScreen";
import {
  HF_BASE,
  PROVINCES_URL,
  METADATA_URL,
  MIN_YEAR,
  MAX_YEAR,
  MAX_ROUNDS,
  OCEAN_COLOR,
  COLOR_MODES,
  pickRandomYear,
  calcScore,
  yearLabel,
  resolveFullName,
  resolveColor,
  computeGroupedLabels,
} from "./mapUtils";

function buildMerged(provinces, yearData, metadata, colorMode) {
  if (!provinces || !yearData || !metadata) return null;
  return {
    ...provinces,
    features: provinces.features.map(f => {
      const info = yearData[f.properties.name];
      if (!info) return f;
      const [rulerKey, cultureKey, religionKey, capital] = info;
      const modeKey = colorMode === "ruler" ? rulerKey : colorMode === "culture" ? cultureKey : religionKey;
      return {
        ...f,
        properties: {
          ...f.properties,
          rulerKey, cultureKey, religionKey,
          rulerName: resolveFullName(metadata, "ruler", rulerKey),
          cultureName: resolveFullName(metadata, "culture", cultureKey),
          religionName: resolveFullName(metadata, "religion", religionKey),
          capital,
          color: resolveColor(metadata, colorMode, modeKey),
        },
      };
    }),
  };
}


export default function Mapguessr() {
  const [dataVersion, setDataVersion] = useState(0);
  const [screen, setScreen] = useState("splash"); // splash | game | explore | gameover | history | leaderboard | profile
  const [profileTarget, setProfileTarget] = useState(null);
  const [provinces, setProvinces] = useState(null);
  const [metadata, setMetadata] = useState(null);

  // Auth
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Current game DB id
  const [gameId, setGameId] = useState(null);

  const [showBorders, setShowBorders] = useState(false);
  const [showMarkers, setShowMarkers] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [colorMode, setColorMode] = useState("ruler");

  const [actualYear, setActualYear] = useState(0);
  const [yearData, setYearData] = useState(null);
  const [markerData, setMarkerData] = useState(null);
  const [guessYear, setGuessYear] = useState(0);
  const [guessInput, setGuessInput] = useState("0");
  const [phase, setPhase] = useState("guessing");
  const [score, setScore] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [round, setRound] = useState(1);

  const [exploreYear, setExploreYear] = useState(1000);
  const [exploreYearData, setExploreYearData] = useState(null);
  const [exploreMarkerData, setExploreMarkerData] = useState(null);

  const [paneOpacity, setPaneOpacity] = useState(1);
  const [tooltip, setTooltip] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const ttTimer = useRef(null);
  const fadeTimer = useRef(null);
  const exploreDebounce = useRef(null);

  const openProfile = useCallback((userId, displayName) => {
    setProfileTarget({ userId, displayName });
    setScreen("profile");
  }, []);

  // ── Auth init ──────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  // ── Map data init ──────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      fetch(PROVINCES_URL).then(r => r.json()),
      fetch(METADATA_URL).then(r => r.json()),
    ]).then(([p, m]) => { setProvinces(p); setMetadata(m); });
  }, []);

  const fadeLoad = useCallback((fetchFn, onData) => {
    clearTimeout(fadeTimer.current);
    setPaneOpacity(0);
    fetchFn().then(data => {
      fadeTimer.current = setTimeout(() => {
        onData(data);
        setPaneOpacity(1);
      }, 300);
    });
  }, []);

  const clampYear = (value) => Math.max(MIN_YEAR, Math.min(MAX_YEAR, Math.round(value)));

  const updateGuessInput = (value) => {
    if (value === "" || value === "-" || value === "+") { setGuessInput(value); return; }
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      const clamped = clampYear(parsed);
      setGuessYear(clamped);
      setGuessInput(String(clamped));
    } else {
      setGuessInput(value);
    }
  };

  const loadGameYear = useCallback((y) => {
    const snap = Math.round(y / 10) * 10;
    fadeLoad(
      () => Promise.all([
        fetch(`${HF_BASE}/attrs/${snap}.json`).then(r => r.ok ? r.json() : {}),
        fetch(`${HF_BASE}/attrs/${snap}_markers.geojson`).then(r => r.ok ? r.json() : null),
      ]),
      ([attrs, markers]) => { setYearData(attrs); setMarkerData(markers); setDataVersion(v => v + 1); }
    );
  }, [fadeLoad]);

  const loadExploreYear = useCallback((y) => {
    const snap = Math.round(y / 10) * 10;
    clearTimeout(exploreDebounce.current);
    exploreDebounce.current = setTimeout(() => {
      fadeLoad(
        () => Promise.all([
          fetch(`${HF_BASE}/attrs/${snap}.json`).then(r => r.ok ? r.json() : {}),
          fetch(`${HF_BASE}/attrs/${snap}_markers.geojson`).then(r => r.ok ? r.json() : null),
        ]),
        ([attrs, markers]) => { setExploreYearData(attrs); setExploreMarkerData(markers); }
      );
    }, 80);
  }, [fadeLoad]);

  const startRound = useCallback((existingGameId) => {
    const y = pickRandomYear();
    setActualYear(y);
    setGuessYear(0);
    setGuessInput("0");
    setScore(null);
    setPhase("guessing");
    setShowResultModal(false);
    setTooltip(null);
    setTooltipVisible(false);
    setMarkerData(null);
    loadGameYear(y);
  }, [loadGameYear]);

  // Start a brand-new game: create DB row if logged in, then start round 1
  const startGame = useCallback(async () => {
    setRound(1);
    setTotalScore(0);
    setScreen("game");
    let gid = null;
    if (user) {
      gid = await dbCreateGame(user.id);
      setGameId(gid);
    } else {
      setGameId(null);
    }
    startRound(gid);
  }, [user, startRound]);

  // ── Confirm guess ──────────────────────────────────────────────────────────
  const confirmGuess = useCallback(async () => {
    const s = calcScore(guessYear, actualYear);
    const newTotal = totalScore + s;
    const newRound = round;

    setScore(s);
    setTotalScore(newTotal);
    setPhase("revealed");
    setShowResultModal(true);

    if (user && gameId) {
      await dbSaveRound(gameId, user.id, newRound, actualYear, guessYear, s);
      const isLastRound = newRound >= MAX_ROUNDS;
      await dbUpdateGame(gameId, newTotal, newRound, isLastRound);
    }
  }, [guessYear, actualYear, totalScore, round, user, gameId]);

  // ── Advance to next round or game over ─────────────────────────────────────
  const advanceGame = useCallback(() => {
    setShowResultModal(false);
    if (round >= MAX_ROUNDS) {
      setScreen("gameover");
    } else {
      setRound(r => r + 1);
      startRound(gameId);
    }
  }, [round, gameId, startRound]);

  // ── Memos ──────────────────────────────────────────────────────────────────
  const gameMerged = useMemo(() => buildMerged(provinces, yearData, metadata, colorMode), [provinces, yearData, metadata, colorMode]);
  const exploreMerged = useMemo(() => buildMerged(provinces, exploreYearData, metadata, colorMode), [provinces, exploreYearData, metadata, colorMode]);

  const activeData = screen === "explore" ? exploreMerged : gameMerged;
  const activeMarkers = screen === "explore" ? exploreMarkerData : markerData;

  const groupedLabels = useMemo(() => {
    if (!activeData) return [];
    return computeGroupedLabels(activeData.features, colorMode);
  }, [activeData, colorMode]);

  const showTT = useCallback((data) => {
    clearTimeout(ttTimer.current);
    setTooltip(data);
    ttTimer.current = setTimeout(() => setTooltipVisible(true), 10);
  }, []);
  const hideTT = useCallback(() => {
    setTooltipVisible(false);
    ttTimer.current = setTimeout(() => setTooltip(null), 280);
  }, []);

  const onEachFeature = useCallback((feature, layer) => {
    layer.on({
      mouseover: () => {
        const p = feature.properties;
        layer.setStyle({ weight: 0.8, color: "#c9a84c" });
        showTT({ name: p.name, ruler: p.rulerName, culture: p.cultureName, religion: p.religionName, capital: p.capital });
      },
      mouseout: () => {
        layer.setStyle({ weight: showBorders ? 0.5 : 0, color: "#000" });
        hideTT();
      }
    });
  }, [showBorders, showTT, hideTT]);

  const styleFeature = useCallback((feature) => ({
    fillColor: feature.properties.color || "#1e3248",
    weight: showBorders ? 0.5 : 0,
    color: "#000",
    fillOpacity: 0.82,
  }), [showBorders]);

  const prevColorMode = useRef(colorMode);
  useEffect(() => {
    if (prevColorMode.current !== colorMode) {
      prevColorMode.current = colorMode;
      setPaneOpacity(0);
      clearTimeout(fadeTimer.current);
      fadeTimer.current = setTimeout(() => setPaneOpacity(1), 350);
    }
  }, [colorMode]);

  const geoKey = `${colorMode}-${showBorders}-${screen === "explore" ? exploreYear : actualYear}-${dataVersion}`;

  // ── Shared styles ──────────────────────────────────────────────────────────
  const btn = (active) => ({
    background: active ? "#c9a84c" : "transparent",
    color: active ? "#0d1117" : "#c9a84c",
    border: `1px solid ${active ? "#c9a84c" : "#3a4a5a"}`,
    padding: "3px 11px", cursor: "pointer", borderRadius: 3,
    fontSize: 11, fontFamily: "Georgia, serif", letterSpacing: "0.05em",
    transition: "all 0.2s ease",
  });

  const scoreColor = (s) => s > 600 ? "#6fcf97" : s > 250 ? "#f0c040" : "#eb5757";

  // ── User avatar button (top-right) ─────────────────────────────────────────
  const UserBadge = () => {
    const [open, setOpen] = useState(false);
    if (authLoading) return null;
    if (!user) return (
      <button onClick={() => setShowAuth(true)} style={{ ...btn(false), fontSize: 10, letterSpacing: 1.5 }}>
        SIGN IN
      </button>
    );
    const name = user.user_metadata?.full_name || user.email?.split("@")[0] || "Player";
    const avatar = user.user_metadata?.avatar_url;
    return (
      <div style={{ position: "relative" }}>
        <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
          {avatar
            ? <img src={avatar} alt="" style={{ width: 24, height: 24, borderRadius: "50%", border: "1px solid #3a4a5a" }} />
            : <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#1a2535", border: "1px solid #3a4a5a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#c9a84c" }}>
                {name[0].toUpperCase()}
              </div>
          }
          <span style={{ fontSize: 10, color: "#c9a84c", letterSpacing: 1 }}>{name}</span>
        </div>
        {open && (
          <div style={{
            position: "absolute", right: 0, top: "calc(100% + 8px)",
            background: "#0f1725", border: "1px solid #1e2e3e", borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)", zIndex: 500, minWidth: 140, overflow: "hidden",
          }}>
            {[
              { label: "Profile", action: () => { openProfile(user.id, name); setOpen(false); } },
              { label: "History", action: () => { setScreen("history"); setOpen(false); } },
              { label: "Leaderboard", action: () => { setScreen("leaderboard"); setOpen(false); } },
              { label: "Sign Out", action: () => { handleSignOut(); setOpen(false); } },
            ].map(({ label, action }) => (
              <div key={label} onClick={action} style={{
                padding: "10px 14px", fontSize: 11, color: "#c9a84c", letterSpacing: 1.5,
                cursor: "pointer", fontFamily: "Georgia, serif",
                borderBottom: label !== "Sign Out" ? "1px solid #1a2530" : "none",
              }}
                onMouseEnter={e => e.currentTarget.style.background = "#1a2535"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {label.toUpperCase()}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Screens that replace the full page ────────────────────────────────────
  if (screen === "history") {
    return user
      ? <HistoryScreen userId={user.id} onBack={() => setScreen("splash")} />
      : null; // shouldn't happen; guard anyway
  }

  if (screen === "leaderboard") {
    return <LeaderboardScreen onBack={() => setScreen("splash")} onViewProfile={openProfile} />;
  }

  if (screen === "profile") {
    return (
      <ProfileScreen
        target={profileTarget}
        provinces={provinces}
        metadata={metadata}
        onBack={() => { setScreen("splash"); setProfileTarget(null); }}
        isCurrentUser={user?.id === profileTarget?.userId}
      />
    );
  }

  if (screen === "splash") return (
    <div style={{ background: "#0a0f16", height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 14, position: "relative" }}>
      {/* top-right auth */}
      <div style={{ position: "absolute", top: 14, right: 16 }}>
        <UserBadge />
      </div>

      <div style={{ fontSize: 36, color: "#c9a84c", letterSpacing: 7, fontFamily: "Georgia, serif", fontWeight: "bold" }}>BORDERLINE</div>
      <div style={{ color: "#4a5a6a", fontSize: 11, letterSpacing: 3 }}>THINGS CHANGE (Mapguessr clone but cooler)</div>
      <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
        <button onClick={startGame} style={{ ...btn(true), padding: "9px 28px", fontSize: 12 }}>PLAY GAME</button>
        <button onClick={() => { setScreen("explore"); loadExploreYear(exploreYear); }}
          style={{ ...btn(false), padding: "9px 28px", fontSize: 12 }}>EXPLORE</button>
        <button onClick={() => setScreen("leaderboard")} style={{ ...btn(false), padding: "9px 28px", fontSize: 12 }}>LEADERBOARD</button>
      </div>
      {!user && (
        <div style={{ fontSize: 10, color: "#4a5a6a", letterSpacing: 2, marginTop: 8 }}>
          <span style={{ color: "#c9a84c", cursor: "pointer", textDecoration: "underline" }} onClick={() => setShowAuth(true)}>
            Sign in
          </span>
          {" "}to save scores & appear on the leaderboard
        </div>
      )}

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onAuth={(u) => { setUser(u); setShowAuth(false); }} />}
    </div>
  );

  if (screen === "gameover") return (
    <div style={{ background: "#0a0f16", height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 14, position: "relative" }}>
      <div style={{ position: "absolute", top: 14, right: 16 }}><UserBadge /></div>
      <div style={{ fontSize: 36, color: "#c9a84c", letterSpacing: 7, fontFamily: "Georgia, serif", fontWeight: "bold" }}>GAME OVER</div>
      <div style={{ fontSize: 18, color: "#aaa", fontFamily: "Georgia, serif" }}>
        Final Score: <span style={{ color: "#c9a84c", fontWeight: "bold" }}>{totalScore}</span> / {MAX_ROUNDS * 1000}
      </div>
      {!user && (
        <div style={{ fontSize: 10, color: "#4a5a6a", letterSpacing: 2 }}>
          <span style={{ color: "#c9a84c", cursor: "pointer", textDecoration: "underline" }} onClick={() => setShowAuth(true)}>
            Sign in
          </span>
          {" "}to save this score
        </div>
      )}
      <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
        <button onClick={startGame} style={{ ...btn(true), padding: "9px 28px", fontSize: 12 }}>PLAY AGAIN</button>
        <button onClick={() => setScreen("splash")} style={{ ...btn(false), padding: "9px 28px", fontSize: 12 }}>MENU</button>
        {user && <button onClick={() => setScreen("history")} style={{ ...btn(false), padding: "9px 28px", fontSize: 12 }}>HISTORY</button>}
      </div>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onAuth={(u) => { setUser(u); setShowAuth(false); }} />}
    </div>
  );

  // ── Main game / explore layout ────────────────────────────────────────────
  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", fontFamily: "Georgia, serif", background: "#0a0f16" }}>
      {/* Header */}
      <div style={{ padding: "7px 14px", background: "#0a0f16", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, borderBottom: "1px solid #1a2530" }}>
        <div style={{ fontSize: 11, color: "#c9a84c", letterSpacing: 2, opacity: 0.65, minWidth: 100 }}>
          {screen === "explore" ? (
            <span style={{ cursor: "pointer", opacity: 0.7 }} onClick={() => setScreen("splash")}>← MENU</span>
          ) : (
            `ROUND ${round}/${MAX_ROUNDS}`
          )}
        </div>

        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
          {COLOR_MODES.map(m => (
            <button key={m} onClick={() => setColorMode(m)} style={btn(colorMode === m)}>
              {m.toUpperCase()}
            </button>
          ))}
          <div style={{ width: 1, height: 14, background: "#2a3a4a", margin: "0 4px" }} />
          <button onClick={() => setShowBorders(b => !b)} style={btn(showBorders)}>BORDERS</button>
          <button onClick={() => setShowMarkers(b => !b)} style={btn(showMarkers)}>MARKERS</button>
          <button onClick={() => setShowLabels(b => !b)} style={btn(showLabels)}>LABELS</button>
        </div>

        <div style={{ minWidth: 100, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 11, color: "#c9a84c", letterSpacing: 2, opacity: 0.65 }}>
            {screen === "explore" ? yearLabel(exploreYear) : `${totalScore} PTS`}
          </div>
          <UserBadge />
        </div>
      </div>

      {/* Map area */}
      <div style={{ flex: 1, overflow: "hidden", minHeight: 0, position: "relative" }}>
        {provinces ? (
          <MapContainer
            center={[20, 0]} zoom={2} minZoom={2}
            maxBounds={[[-90, -180], [90, 180]]}
            maxBoundsViscosity={1.0}
            style={{ height: "100%", width: "100%", background: OCEAN_COLOR }}
          >
            <MapContent
              mergedGeoJSON={activeData}
              groupedLabels={groupedLabels}
              showLabels={showLabels}
              showMarkers={showMarkers}
              markerData={activeMarkers}
              showBorders={showBorders}
              colorMode={colorMode}
              geoKey={geoKey}
              onEachFeature={onEachFeature}
              styleFeature={styleFeature}
              paneOpacity={paneOpacity}
            />
          </MapContainer>
        ) : (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0f16", color: "#c9a84c", fontSize: 11, letterSpacing: 4 }}>
            LOADING
          </div>
        )}

        {/* Province tooltip */}
        {tooltip && (
          <div style={{
            position: "absolute", top: 12, right: 12,
            background: "rgba(6,10,16,0.9)", backdropFilter: "blur(8px)",
            color: "#e8dcc8", padding: "12px 16px", borderRadius: 4,
            border: "1px solid #1e2e3e", zIndex: 1000, minWidth: 210, pointerEvents: "none",
            opacity: tooltipVisible ? 1 : 0,
            transform: tooltipVisible ? "translateY(0px)" : "translateY(-5px)",
            transition: "opacity 0.22s ease, transform 0.22s ease",
          }}>
            <div style={{ fontSize: 9, color: "#c9a84c", textTransform: "uppercase", letterSpacing: 3, marginBottom: 6 }}>{colorMode}</div>
            <div style={{ fontWeight: "bold", fontSize: 15, marginBottom: 10, color: "#fff", lineHeight: 1.3 }}>
              {colorMode === "ruler" ? tooltip.ruler : colorMode === "culture" ? tooltip.culture : tooltip.religion}
            </div>
            <div style={{ height: 1, background: "linear-gradient(to right, #2a3a4a, transparent)", marginBottom: 9 }} />
            <div style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 5 }}>
              {[["Region", tooltip.name], ["Ruler", tooltip.ruler], ["Religion", tooltip.religion], ["Culture", tooltip.culture], tooltip.capital && ["Capital", tooltip.capital]]
                .filter(Boolean)
                .map(([k, v]) => (
                  <div key={k} style={{ display: "flex", gap: 8 }}>
                    <span style={{ color: "#4a5a6a", minWidth: 52, flexShrink: 0 }}>{k}</span>
                    <span style={{ color: "#c8bea8" }}>{v}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Result modal */}
        {screen === "game" && phase === "revealed" && showResultModal && (
          <div style={{
            position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center",
            background: "rgba(6,10,16,0.75)", zIndex: 1100, padding: 20,
          }}>
            <div style={{ width: "min(420px,100%)", background: "#0f1725", border: "1px solid #1e2e3e", borderRadius: 14, boxShadow: "0 15px 40px rgba(0,0,0,0.45)", padding: 24, color: "#e8dcc8" }}>
              <div style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: 2, color: "#c9a84c", marginBottom: 14 }}>Round {round} result</div>
              <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#9aa6b8" }}><span>Actual</span><span>{yearLabel(actualYear)}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#9aa6b8" }}><span>Your guess</span><span>{yearLabel(guessYear)}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#9aa6b8" }}><span>Points</span><span style={{ color: scoreColor(score) }}>{score}</span></div>
              </div>
              {!user && (
                <div style={{ fontSize: 10, color: "#4a5a6a", letterSpacing: 1.5, marginBottom: 14, textAlign: "center" }}>
                  <span style={{ color: "#c9a84c", cursor: "pointer", textDecoration: "underline" }} onClick={() => setShowAuth(true)}>
                    Sign in
                  </span>
                  {" "}to save your scores
                </div>
              )}
              <button onClick={advanceGame} style={{ ...btn(true), width: "100%", padding: "10px 0", fontSize: 12, letterSpacing: 2 }}>
                {round >= MAX_ROUNDS ? "RESULTS" : "NEXT →"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer controls */}
      <div style={{ padding: "10px 16px", background: "#0a0f16", borderTop: "1px solid #1a2530", flexShrink: 0 }}>
        {screen === "explore" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <input type="range" min={MIN_YEAR} max={MAX_YEAR} step={1} value={exploreYear}
              onChange={e => { const y = Number(e.target.value); setExploreYear(y); loadExploreYear(y); }}
              style={{ width: "100%", accentColor: "#c9a84c", cursor: "pointer" }} />
            <div style={{ textAlign: "center", color: "#c9a84c", fontWeight: "bold", fontSize: 15, letterSpacing: 3 }}>
              {yearLabel(exploreYear)}
            </div>
          </div>
        )}

        {screen === "game" && phase === "guessing" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input type="range" min={MIN_YEAR} max={MAX_YEAR} step={1} value={guessYear}
                onChange={e => { const y = Number(e.target.value); setGuessYear(y); setGuessInput(String(y)); }}
                style={{ flex: 1, accentColor: "#c9a84c", cursor: "pointer" }} />
              <input type="number" min={MIN_YEAR} max={MAX_YEAR} step={1} value={guessInput}
                onChange={e => updateGuessInput(e.target.value)}
                onBlur={() => { if (guessInput === "" || guessInput === "-" || guessInput === "+") { setGuessYear(0); setGuessInput("0"); } }}
                className="year-input"
                style={{ width: 100, background: "#0a0f16", color: "#e8dcc8", border: "1px solid #3a4a5a", borderRadius: 5, padding: "8px 10px", fontSize: 12 }} />
            </div>
            <div style={{ textAlign: "center", color: "#c9a84c", fontWeight: "bold", fontSize: 15, letterSpacing: 3 }}>
              {yearLabel(guessYear)}
            </div>
            <button onClick={confirmGuess} style={{ ...btn(true), width: "100%", padding: "7px", fontSize: 11, letterSpacing: 3 }}>
              CONFIRM GUESS
            </button>
          </div>
        )}

        {screen === "game" && phase === "revealed" && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 11, display: "flex", gap: 20, color: "#6a7a8a", letterSpacing: 1.5 }}>
              <span>ACTUAL <strong style={{ color: "#c9a84c" }}>{yearLabel(actualYear)}</strong></span>
              <span>GUESS <strong style={{ color: "#e8dcc8" }}>{yearLabel(guessYear)}</strong></span>
              <span>SCORE <strong style={{ color: scoreColor(score) }}>{score}</strong></span>
            </div>
            <button onClick={advanceGame} style={{ ...btn(true), padding: "7px 18px", fontSize: 11, letterSpacing: 2 }}>
              {round >= MAX_ROUNDS ? "RESULTS" : "NEXT →"}
            </button>
          </div>
        )}
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onAuth={(u) => { setUser(u); setShowAuth(false); }} />}

      <style>{`
        .region-label { background: none !important; border: none !important; box-shadow: none !important; padding: 0 !important; }
        .region-label::before { display: none !important; }
        .leaflet-tooltip.region-label { pointer-events: none !important; }
        .leaflet-container { font-family: Georgia, serif; }
        input.year-input::-webkit-outer-spin-button,
        input.year-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input.year-input { -moz-appearance: textfield; appearance: textfield; }
        .map-label {
          font-weight: 700; color: rgba(255,255,255,0.92);
          text-shadow: 0 0 8px rgba(0,0,0,0.95), 0 0 3px rgba(0,0,0,0.95);
          white-space: nowrap; letter-spacing: 0.09em; text-transform: uppercase;
          pointer-events: none; font-family: Georgia, 'Times New Roman', serif;
          transition: opacity 0.35s ease, font-size 0.3s ease; opacity: 1;
        }
        .map-label-fade { opacity: 0; transition: opacity 0.15s ease; }
      `}</style>
    </div>
  );
}